import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, TrendingUp, Users, Play, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import Navigation from '@/components/Navigation';
import SongCard from '@/components/SongCard';
import { useStore } from '@/lib/store';
import { useMemo, useEffect, useState } from 'react';
import { usePlayback } from '@/lib/playback';
import { extractYouTubeId, getYouTubeThumbnail } from '@/lib/utils';
import * as api from '@/lib/api';
import type { Vote } from '@/lib/mock-data';

const RoundResults = () => {
  const { leagueId, roundId } = useParams<{ leagueId: string; roundId: string }>();
  const navigate = useNavigate();
  const { state, getSubmissionsByRound, getVotesByRound, addVotesToState } = useStore();
  const { currentUser } = state;
  const playback = usePlayback();
  const [votesLoaded, setVotesLoaded] = useState(false);

  // Find the league and round
  const league = useMemo(() => {
    return state.leagues.find(l => l.id === leagueId);
  }, [state.leagues, leagueId]);
  
  const round = useMemo(() => {
    return state.rounds.find(r => r.id === roundId);
  }, [state.rounds, roundId]);
  
  const [isCheckingData, setIsCheckingData] = useState(false);
  
  // Redirect to dashboard if league or round not found (especially on refresh)
  useEffect(() => {
    if (!leagueId || !roundId) return;
    
    // If league or round is not in store, try fetching them immediately
    if ((!league || !round) && !isCheckingData) {
      setIsCheckingData(true);
      const fetchData = async () => {
        try {
          // Fetch league if missing
          if (!league && leagueId) {
            const fetchedLeague = await api.leagueAPI.getLeague(leagueId);
            if (!fetchedLeague) {
              console.log('[RoundResults] League not found, redirecting to dashboard');
              navigate('/dashboard', { replace: true });
              return;
            }
          }
          
          // Fetch round if missing
          if (!round && roundId) {
            const fetchedRound = await api.roundAPI.getRound(roundId);
            if (!fetchedRound) {
              console.log('[RoundResults] Round not found, redirecting to dashboard');
              navigate('/dashboard', { replace: true });
              return;
            }
          }
          
          setIsCheckingData(false);
        } catch (error) {
          console.error('[RoundResults] Error fetching data:', error);
          navigate('/dashboard', { replace: true });
        }
      };
      
      fetchData();
    } else if (league && round) {
      setIsCheckingData(false);
    }
  }, [leagueId, roundId, league, round, navigate, isCheckingData]);
  
  // Get submissions for this round and sort by points
  const submissions = useMemo(() => {
    if (!roundId) return [];
    return getSubmissionsByRound(roundId).sort((a, b) => b.total_points - a.total_points);
  }, [roundId, getSubmissionsByRound]);

  const playlistTracks = useMemo(() => {
    return submissions
      .map((submission) => {
        const youtubeId =
          submission.youtube_video_id ?? extractYouTubeId(submission.video_url ?? '');
        if (!youtubeId) {
          return null;
        }
        return {
          submissionId: submission.id,
          roundId: submission.round_id,
          leagueId: league?.id,
          title: submission.song_title,
          artist: submission.artist_name,
          youtubeId,
          submitter: submission.username || 'Unknown DJ',
          comment: submission.submitter_comment,
          thumbnail: submission.video_thumbnail || getYouTubeThumbnail(youtubeId, 'mqdefault'),
          roundNumber: round?.round_number,
        };
      })
      .filter((track): track is NonNullable<typeof track> => Boolean(track));
  }, [league?.id, round?.round_number, submissions]);

  const winner = submissions[0];
  const secondPlace = submissions[1];
  const thirdPlace = submissions[2];

  // Find user info for winner
  const winnerUser = useMemo(() => {
    return winner ? state.users.find(u => u.id === winner.user_id) : undefined;
  }, [winner, state.users]);
  
  const isCurrentUserWinner = winner?.user_id === currentUser?.id;

  // Fetch votes for completed rounds
  useEffect(() => {
    if (!roundId || !round || round.status !== 'completed' || votesLoaded) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;

    const fetchVotes = async () => {
      try {
        const votes = await api.voteAPI.getVotesByRound(roundId);
        if (votes.length > 0) {
          addVotesToState(votes);
        }
        setVotesLoaded(true);
      } catch (error) {
        console.error('Failed to fetch votes:', error);
        setVotesLoaded(true);
      }
    };

    fetchVotes();
  }, [roundId, round, votesLoaded, addVotesToState]);

  // Get votes for each submission
  const votesBySubmission = useMemo(() => {
    if (!roundId) return new Map<string, Vote[]>();
    // Filter votes directly from state to ensure memo updates when votes are added
    const allVotes = state.votes.filter(v => v.round_id === roundId && v.is_submitted);
    const map = new Map<string, Vote[]>();
    allVotes.forEach(vote => {
      const existing = map.get(vote.submission_id) || [];
      map.set(vote.submission_id, [...existing, vote]);
    });
    console.log('[RoundResults] votesBySubmission updated', {
      roundId,
      totalVotes: allVotes.length,
      submissionsWithVotes: map.size,
      map: Array.from(map.entries()).map(([subId, votes]) => ({ subId, voteCount: votes.length }))
    });
    return map;
  }, [roundId, state.votes]);

  // Don't show "not found" immediately - let the redirect logic handle it
  // This prevents flashing the error message before redirect
  if (!league || !round) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if voting is still open
  const isVotingOpen = round.status === 'voting';
  const isCompleted = round.status === 'completed';
  const deadline = round ? new Date(round.voting_deadline) : null;
  const now = new Date();
  const isDeadlinePassed = deadline ? now > deadline : false;
  // Voting is still open only if status is 'voting' AND deadline hasn't passed
  // For completed rounds, always show submitter info and comments
  const votingStillOpen = isVotingOpen && !isDeadlinePassed && !isCompleted;

  // Calculate total votes received (only from submitted votes)
  const totalVotes = submissions.reduce((sum, sub) => sum + sub.total_points, 0);

  const getMedalColor = (position: number) => {
    if (position === 1) return 'bg-yellow-500';
    if (position === 2) return 'bg-gray-400';
    if (position === 3) return 'bg-orange-500';
    return 'bg-muted';
  };

  const getMedalIcon = (position: number) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
      <Navigation />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(`/leagues/${leagueId}`)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to League
          </Button>
          
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Round Results</h1>
              <p className="text-muted-foreground">
                {round.theme}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                console.log('[RoundResults] Play playlist clicked', { 
                  trackCount: playlistTracks.length,
                  tracks: playlistTracks.map(t => ({ title: t.title, youtubeId: t.youtubeId }))
                });
                if (playlistTracks.length === 0) {
                  console.warn('[RoundResults] No tracks to play');
                  return;
                }
                try {
                  playback.setPlaylist(playlistTracks, {
                    id: `round_${round.id}_results`,
                    label: `${league?.name || 'Round'} • ${round.theme}`,
                    autoplay: false,
                    shuffle: false,
                  });
                  console.log('[RoundResults] Playlist set successfully');
                  // Navigate to playlist player page
                  navigate('/playlists/player');
                } catch (error) {
                  console.error('[RoundResults] Error setting playlist:', error);
                }
              }}
              disabled={playlistTracks.length === 0}
            >
              <Play className="mr-2 h-4 w-4" />
              Play round playlist
            </Button>
          </div>
        </div>

        {/* Voting Still Open Banner */}
        {votingStillOpen && (
          <Alert className="mb-6 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <strong>Voting is still in progress.</strong> Current scores are shown below, but submitter names and comments are hidden until voting closes. Final results will be revealed when voting ends.
            </AlertDescription>
          </Alert>
        )}

        {/* Round Info */}
        <Card className="mb-6 border-2 border-primary/20 bg-gradient-to-br from-purple-50/30 to-blue-50/30 dark:from-purple-950/15 dark:to-blue-950/15 shadow-md">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Theme</p>
                <p className="text-lg font-semibold text-foreground">{round.theme}</p>
                {round.description && (
                  <p className="text-sm text-muted-foreground mt-1">{round.description}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Round Status</p>
                <Badge variant="secondary" className="status-completed">
                  Completed
                </Badge>
                <p className="text-sm text-muted-foreground mt-2">
                  {submissions.length} submission{submissions.length !== 1 ? 's' : ''} received
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Winner Spotlight */}
        {winner && (
          <Card className={`mb-6 border-2 ${
            isCurrentUserWinner 
              ? 'border-primary bg-primary/5' 
              : 'border-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/10'
          }`}>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <div className={`relative w-24 h-24 rounded-full ${getMedalColor(1)} flex items-center justify-center text-4xl`}>
                  🥇
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                    <Trophy className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <h2 className="text-2xl font-bold text-foreground">Round Winner</h2>
                    {isCurrentUserWinner && (
                      <Badge className="bg-primary text-primary-foreground">You!</Badge>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-1">
                    {winner.song_title}
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    by {winner.artist_name}
                  </p>
                  {!votingStillOpen && (winnerUser || winner.username) && (
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                      {winnerUser && (
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={winnerUser.avatar_url} />
                          <AvatarFallback className="bg-music-gradient text-white text-xs">
                            {winnerUser.username.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <span className="text-sm font-medium text-foreground">
                        {winner.username || winnerUser?.username || 'Unknown DJ'}
                      </span>
                    </div>
                  )}
                  {votingStillOpen && (
                    <p className="text-sm text-muted-foreground italic mb-3">Submitter hidden until voting closes</p>
                  )}
                  <div className="flex items-center justify-center md:justify-start gap-4">
                    <div className="flex items-center gap-1">
                      <Trophy className="h-4 w-4 text-primary" />
                      <span className="text-lg font-bold text-primary">{winner.total_points} points</span>
                    </div>
                    {totalVotes > 0 && (
                      <div className="text-sm text-muted-foreground">
                        {((winner.total_points / totalVotes) * 100).toFixed(1)}% of total votes
                      </div>
                    )}
                  </div>
                  {!votingStillOpen && winner.submitter_comment && (
                    <div className="mt-3 p-3 bg-background rounded-lg border">
                      <p className="text-sm text-muted-foreground italic">
                        "{winner.submitter_comment}"
                      </p>
                    </div>
                  )}
                  {votingStillOpen && winner.submitter_comment && (
                    <div className="mt-3 p-3 bg-background rounded-lg border">
                      <p className="text-sm text-muted-foreground italic">
                        Comment hidden until voting closes
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Podium (Top 3) */}
        {submissions.length >= 3 && (
          <Card className="mb-6 border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
            <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
              <CardTitle className="flex items-center text-primary">
                <Medal className="h-5 w-5 mr-2" />
                Top 3
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {[secondPlace, winner, thirdPlace].map((submission, idx) => {
                  if (!submission) return null;
                  const actualPosition = idx === 0 ? 2 : idx === 1 ? 1 : 3;
                  const user = state.users.find(u => u.id === submission.user_id);
                  
                  return (
                    <Card
                      key={submission.id}
                      className={`text-center ${
                        actualPosition === 1
                          ? 'border-2 border-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/10'
                          : ''
                      }`}
                    >
                      <CardContent className="pt-6">
                        <div className={`inline-flex w-12 h-12 rounded-full ${getMedalColor(actualPosition)} items-center justify-center text-2xl mb-3`}>
                          {getMedalIcon(actualPosition)}
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">#{actualPosition}</p>
                        <h4 className="font-semibold text-foreground mb-1">{submission.song_title}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{submission.artist_name}</p>
                        {!votingStillOpen && (user || submission.username) && (
                          <div className="flex items-center justify-center gap-1 mb-2">
                            {user && (
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={user.avatar_url} />
                                <AvatarFallback className="bg-music-gradient text-white text-xs">
                                  {user.username.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {submission.username || user?.username || 'Unknown DJ'}
                            </span>
                          </div>
                        )}
                        {votingStillOpen && (
                          <p className="text-xs text-muted-foreground italic mb-2">Submitter hidden</p>
                        )}
                        <div className="text-lg font-bold text-primary">{submission.total_points} pts</div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Full Rankings */}
        <Card className="border-2 border-blue-300/40 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 dark:from-blue-950/20 dark:to-indigo-950/20 shadow-md">
          <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-blue-300/40">
            <CardTitle className="flex items-center text-blue-600 dark:text-blue-400">
              <TrendingUp className="h-5 w-5 mr-2" />
              Full Rankings
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              All submissions ranked by total points received
            </p>
          </CardHeader>
          <CardContent>
            {submissions.length > 0 ? (
              <div className="space-y-4">
                {submissions.map((submission, index) => {
                  const user = state.users.find(u => u.id === submission.user_id);
                  const isCurrentUserSubmission = currentUser ? submission.user_id === currentUser.id : false;
                  const percentage = totalVotes > 0 ? (submission.total_points / totalVotes) * 100 : 0;

                  return (
                    <div
                      key={submission.id}
                      className={`p-4 rounded-lg border ${
                        isCurrentUserSubmission
                          ? 'bg-primary/10 border-primary/20'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-500 text-white' :
                          index === 1 ? 'bg-gray-400 text-white' :
                          index === 2 ? 'bg-orange-500 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-foreground">{submission.song_title}</h4>
                            {isCurrentUserSubmission && (
                              <Badge variant="secondary" className="text-xs">Your submission</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">by {submission.artist_name}</p>
                          {!votingStillOpen && (user || submission.username) && (
                            <div className="flex items-center gap-2">
                              {user && (
                                <Avatar className="h-5 w-5">
                                  <AvatarImage src={user.avatar_url} />
                                  <AvatarFallback className="bg-music-gradient text-white text-xs">
                                    {user.username.charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {submission.username || user?.username || 'Unknown DJ'}
                              </span>
                            </div>
                          )}
                          {votingStillOpen && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground italic">Submitter hidden until voting closes</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-primary">{submission.total_points}</div>
                            <div className="text-xs text-muted-foreground">points</div>
                            {totalVotes > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {percentage.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {!votingStillOpen && submission.submitter_comment && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-muted-foreground italic">
                            "{submission.submitter_comment}"
                          </p>
                        </div>
                      )}
                      {votingStillOpen && submission.submitter_comment && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-muted-foreground italic">
                            Comment hidden until voting closes
                          </p>
                        </div>
                      )}
                      
                      {/* Show individual votes for completed rounds */}
                      {(() => {
                        const hasVotes = votesBySubmission.has(submission.id);
                        const voteCount = votesBySubmission.get(submission.id)?.length || 0;
                        console.log('[RoundResults] Vote display check', {
                          submissionId: submission.id,
                          songTitle: submission.song_title,
                          votingStillOpen,
                          isCompleted,
                          hasVotes,
                          voteCount,
                          shouldShow: !votingStillOpen && isCompleted && hasVotes
                        });
                        return !votingStillOpen && isCompleted && hasVotes;
                      })() && (
                        <div className="mt-3 pt-3 border-t border-primary/20">
                          <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                            Votes ({votesBySubmission.get(submission.id)?.length || 0})
                          </p>
                          <div className="space-y-2">
                            {votesBySubmission.get(submission.id)?.map((vote) => {
                              const voter = state.users.find(u => u.id === vote.voter_id);
                              return (
                                <div key={vote.id} className="flex items-start gap-2 text-sm">
                                  <div className="flex items-center gap-2 flex-1">
                                    {voter && (
                                      <Avatar className="h-4 w-4">
                                        <AvatarImage src={voter.avatar_url} />
                                        <AvatarFallback className="bg-music-gradient text-white text-[8px]">
                                          {voter.username.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                    <span className="font-medium text-foreground">
                                      {vote.username || voter?.username || 'Unknown'}
                                    </span>
                                    <span className="text-muted-foreground">•</span>
                                    <span className="text-primary font-semibold">
                                      {vote.points} {vote.points === 1 ? 'point' : 'points'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                            {votesBySubmission.get(submission.id)?.some(v => v.comment) && (
                              <div className="mt-2 space-y-1 pl-6">
                                {votesBySubmission.get(submission.id)?.map((vote) => {
                                  if (!vote.comment) return null;
                                  const voter = state.users.find(u => u.id === vote.voter_id);
                                  return (
                                    <div key={vote.id} className="text-xs text-muted-foreground italic">
                                      <span className="font-medium">{vote.username || voter?.username || 'Unknown'}:</span> "{vote.comment}"
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No submissions for this round.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {playlistTracks.length > 0 && (
          <Card className="mt-6 border-2 border-indigo-300/40 bg-gradient-to-br from-indigo-100/30 to-purple-100/30 dark:from-indigo-950/15 dark:to-purple-950/15 shadow-md">
            <CardHeader className="bg-gradient-to-r from-accent/15 via-primary/15 to-indigo-500/15 dark:from-accent/20 dark:via-primary/20 dark:to-indigo-400/20 rounded-t-lg border-b border-indigo-300/40">
              <CardTitle className="flex items-center text-indigo-600 dark:text-indigo-400">
                <Play className="h-5 w-5 mr-2" />
                Watch &amp; Replay Submissions
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Click any submission to play it instantly in the playlist player.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {submissions.map((submission) => (
                  <SongCard
                    key={`watch-${submission.id}`}
                    submission={submission}
                    showSubmitter={!votingStillOpen}
                    showPoints
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statistics Card */}
        {submissions.length > 0 && (
          <Card className="mt-6 border-2 border-secondary/30 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-950/15 dark:to-indigo-950/15 shadow-md">
            <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-secondary/30">
              <CardTitle className="flex items-center text-blue-600 dark:text-blue-400">
                <Users className="h-5 w-5 mr-2" />
                Round Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-primary">{submissions.length}</p>
                  <p className="text-sm text-muted-foreground">Total Submissions</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-primary">{totalVotes}</p>
                  <p className="text-sm text-muted-foreground">Total Points Allocated</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-primary">
                    {totalVotes > 0 ? (totalVotes / submissions.length).toFixed(1) : '0'}
                  </p>
                  <p className="text-sm text-muted-foreground">Avg Points Per Submission</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <Button onClick={() => navigate(`/leagues/${leagueId}`)} className="btn-music">
            Back to League
          </Button>
          {league.current_round && league.current_round.id !== roundId && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/leagues/${leagueId}/rounds/${league.current_round!.id}/vote`)}
            >
              View Current Round
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default RoundResults;


